/**
 * TransferEverythingModal — "Transfer everything from a person" (wireframe admin-console-wireframe; Ownership design,
 * part 4). Pick the current owner (each with how many JDs they own), see what they own (in-review JDs marked), pick the
 * new owner over the whole HR pool with the reasons worked out across ALL of those JDs ("This is the current owner"
 * included), the same carry-over choice and banner, then "Transfer N items". Jobs Library JDs only for now (People + AI
 * is coming soon); archived JDs are not listed (the server skips them too).
 *   open, rows (every JD on the page), transferring, onCancel(), onTransfer({ rows, newOwner, carry, newOwnerName })
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { SbButton, SbModal, SbSelect, SbTooltip, TooltipTriggerStyled } from 'uiCore';
import { getTransferCandidates } from 'Api/apiService';
import {
  CANDIDATES_LOAD_FAILED, CARRY, CURRENT_OWNER_HELP, CURRENT_OWNER_LABEL, CURRENT_OWNER_PLACEHOLDER, PICK_BOTH, PICK_CURRENT_FIRST,
  TRANSFER_ALL_TITLE, countPeople, getAdminError, getAffectedNames, getAllBanner,
  getAllEligibleHelp, getOwnedRows, getOwnerChoices, getOwnsText, getTransferItemsButton, isInReview, personName,
} from 'Utils/ownershipTransfer';
import { TransferFieldLabel, TransferHelperText } from 'Pages/PeoplePlusAIV2/dashboard/ManageGroupsModal.styles';
import {
  ConflictItemMetaStyled, ConflictProjectItemStyled, ConflictProjectListStyled, ConflictProjectNameStyled,
} from 'Components/EmployeeFunctionAccess/EmployeeFunctionAccess.styles';
import { CarryChoice, NewOwnerField, PermanenceBanner } from './TransferParts';

const SHOWN = 4;

const TransferEverythingModal = ({ open, rows = [], transferring = false, onCancel, onTransfer }) => {
  const [from, setFrom] = useState(null);
  const [newOwner, setNewOwner] = useState(null);
  const [carry, setCarry] = useState(CARRY);
  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!open) return;
    setError(null);
    setFrom(null);
    setNewOwner(null);
    setCarry(CARRY);
    setCandidates(null);
  }, [open]);

  const owned = from ? getOwnedRows(rows, from) : [];
  const choices = getOwnerChoices(rows);
  const fromName = choices.find((c) => c.value === from)?.name || from;

  useEffect(() => {
    if (!open || !from) return undefined;
    let cancelled = false;
    setNewOwner(null);
    setCandidates(null);
    setError(null);
    setLoading(true);
    getTransferCandidates(getOwnedRows(rows, from).map((r) => r.job_id), from)
      .then((data) => { if (!cancelled) setCandidates(data); })
      .catch((err) => { if (!cancelled) setError(getAdminError(err, CANDIDATES_LOAD_FAILED)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, from]); // eslint-disable-line react-hooks/exhaustive-deps -- rows: the page's, the same while open

  const { ok, total } = countPeople(candidates);
  const ready = Boolean(from && newOwner && owned.length);
  return (
    <SbModal
      open={open}
      title={TRANSFER_ALL_TITLE}
      onCancel={transferring ? undefined : onCancel}
      maskClosable={false}
      destroyOnClose
      width={620}
      footer={[
        <SbButton key="cancel" buttonType="secondary" disabled={transferring} onClick={onCancel}>Cancel</SbButton>,
        <SbTooltip key="ok" content={PICK_BOTH} disabled={ready}>
          <TooltipTriggerStyled>
            <SbButton buttonType="primary" disabled={!ready || transferring} loading={transferring}
              onClick={() => onTransfer({ rows: owned, newOwner, carry: carry === CARRY,
                newOwnerName: personName(candidates, newOwner) })}>
              {getTransferItemsButton(owned.length)}
            </SbButton>
          </TooltipTriggerStyled>
        </SbTooltip>,
      ]}
    >
      <>
        <TransferFieldLabel>{CURRENT_OWNER_LABEL}</TransferFieldLabel>
        <SbSelect options={choices} value={from || undefined} onChange={(v) => setFrom(v || null)}
          placeholder={CURRENT_OWNER_PLACEHOLDER} showSearch optionFilterProp="label" allowClear
          disabled={transferring} style={{ width: '100%' }} getPopupContainer={(trigger) => trigger.parentNode}
          aria-label={CURRENT_OWNER_LABEL} />
        <TransferHelperText>{CURRENT_OWNER_HELP}</TransferHelperText>
      </>
      {from && (
        <div data-testid="owned-items">
          <TransferFieldLabel>{getOwnsText(fromName, owned.length)}</TransferFieldLabel>
          <ConflictProjectListStyled>
            {owned.slice(0, SHOWN).map((r) => (
              <ConflictProjectItemStyled key={r.job_id}>
                <ConflictProjectNameStyled>{r.title || r.sb_requisition_number}</ConflictProjectNameStyled>
                <ConflictItemMetaStyled>
                  {r.sb_requisition_number || r.job_id}{isInReview(r) ? ' · in review' : ''}
                </ConflictItemMetaStyled>
              </ConflictProjectItemStyled>
            ))}
            {owned.length > SHOWN && <ConflictProjectItemStyled>and {owned.length - SHOWN} more</ConflictProjectItemStyled>}
          </ConflictProjectListStyled>
        </div>
      )}
      <NewOwnerField candidates={candidates} loading={loading} value={newOwner} onChange={setNewOwner} error={error}
        disabled={!from || transferring || (candidates && !ok)}
        helpBelow={!from ? PICK_CURRENT_FIRST : candidates ? getAllEligibleHelp(ok, total) : null} />
      <CarryChoice value={carry} onChange={setCarry} affectedNames={getAffectedNames(owned)} />
      {from && <PermanenceBanner text={getAllBanner(fromName)} />}
    </SbModal>
  );
};

TransferEverythingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  transferring: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onTransfer: PropTypes.func.isRequired,
};

export default TransferEverythingModal;
